import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";

const INK = "#1F2937";
const SUB = "#6B7280";
// One distinct hue per wedge. These colours double as the bold wedge labels,
// so they're chosen to clear ≥3:1 text contrast AND adjacent-pair CVD in light
// mode (validated with the dataviz palette validator).
const PALETTE = [
  "#16A085", // teal
  "#2F6FED", // blue
  "#C77A00", // amber
  "#B5327E", // magenta
  "#2E7D32", // green
  "#D9642A", // orange
];

export type NightingaleDatum = { name: string; value: number };

type NightingaleChartProps = {
  data: NightingaleDatum[];
  /** Slice whose label is emphasised (bold); defaults to the largest value. */
  highlightName?: string;
  /** Per-wedge colours; falls back to the built-in categorical palette. */
  palette?: string[];
  valueFormatter?: (v: number) => string;
  headline?: string;
  subheadline?: string;
};

// Fixed drawing frame; the SVG scales responsively via viewBox. Width is wider
// than tall to leave horizontal room for the outer labels so they never clip.
const W = 620;
const H = 380;
const CX = W / 2;
const CY = H / 2 + 6;
const MAX_R = 116; // largest wedge radius
const RING_R = MAX_R + 16; // radius the horizontal labels sit on
const MIN_R = 10; // floor so a tiny value is still a visible (clickable) nub

const toRad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Nightingale / rose / polar-area chart. Equal-angle wedges (one per
 * category); radius encodes magnitude, scaled by sqrt(value) so the wedge
 * AREA — what the eye actually reads — stays proportional to the value (a
 * plain linear radius exaggerates, since area grows with r²). One slice
 * carries the accent colour (emphasis; the rest are gray context) and every
 * slice is direct-labelled with its value on a clean outer ring joined by a
 * thin leader — so the number is always readable even when its wedge is tiny.
 */
const NightingaleChart = ({
  data,
  highlightName,
  palette = PALETTE,
  valueFormatter = (v) => v.toLocaleString("en-IN"),
  headline,
  subheadline,
}: NightingaleChartProps) => {
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);

  const rows = useMemo(
    () => data.filter((d) => d.value !== undefined && d.value !== null),
    [data],
  );
  const maxValue = Math.max(1, ...rows.map((r) => Math.abs(r.value)));

  const resolvedHighlight = useMemo(() => {
    if (highlightName) return highlightName;
    return rows.reduce(
      (best, r) => (r.value > (best?.value ?? -Infinity) ? r : best),
      rows[0],
    )?.name;
  }, [rows, highlightName]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.value - a.value),
    [rows],
  );
  const lead = rows.find((r) => r.name === resolvedHighlight) ?? sorted[0];
  const others = sorted.find((r) => r.name !== lead?.name);
  const autoHeadline = lead ? `${lead.name} is the largest at ${valueFormatter(lead.value)}` : "";
  const autoSubheadline =
    lead && others && others.value
      ? `${Math.round(((lead.value - others.value) / others.value) * 100)}% above ${
          others.name
        } (${valueFormatter(others.value)})`
      : "";

  const n = rows.length;
  const anglePerSlice = n ? 360 / n : 0;
  const gapDeg = n > 1 ? Math.min(3, anglePerSlice * 0.08) : 0;

  const activeName = focused ?? hovered;

  const radiusFor = (value: number) =>
    Math.max(MIN_R, MAX_R * Math.sqrt(Math.abs(value) / maxValue));

  const wedges = rows.map((row, i) => {
    // 0° = 12 o'clock, sweeping clockwise (screen y-down).
    const slotStart = i * anglePerSlice - 90;
    const slotEnd = slotStart + anglePerSlice;
    const startDeg = slotStart + gapDeg / 2;
    const endDeg = slotEnd - gapDeg / 2;
    const midDeg = (slotStart + slotEnd) / 2;
    const midRad = toRad(midDeg);

    const r = radiusFor(row.value);
    const s = toRad(startDeg);
    const e = toRad(endDeg);
    const x1 = CX + r * Math.cos(s);
    const y1 = CY + r * Math.sin(s);
    const x2 = CX + r * Math.cos(e);
    const y2 = CY + r * Math.sin(e);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    const path = `M ${CX},${CY} L ${x1},${y1} A ${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;

    // Label sits on a fixed outer ring, kept horizontal; a thin leader joins
    // the wedge tip to the label so tiny wedges stay connected to their value.
    const cos = Math.cos(midRad);
    const lx = CX + RING_R * cos;
    const ly = CY + RING_R * Math.sin(midRad);
    const anchor: "start" | "middle" | "end" =
      cos > 0.25 ? "start" : cos < -0.25 ? "end" : "middle";
    const tx = lx + (anchor === "start" ? 4 : anchor === "end" ? -4 : 0);
    const leaderX = CX + (r + 2) * cos;
    const leaderY = CY + (r + 2) * Math.sin(midRad);

    return {
      ...row,
      path,
      color: palette[i % palette.length],
      isHighlight: row.name === resolvedHighlight,
      tx,
      ly,
      anchor,
      leaderX,
      leaderY,
      lEndX: CX + (RING_R - 3) * cos,
      lEndY: CY + (RING_R - 3) * Math.sin(midRad),
    };
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: INK }}>
          {headline ?? autoHeadline}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: SUB }}>
          {(subheadline ?? autoSubheadline) + " · click a slice to focus"}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          onMouseLeave={() => setHovered(null)}
          style={{ maxHeight: "100%" }}
        >
          {/* faint reference rings for scale */}
          {[0.5, 1].map((f) => (
            <circle
              key={f}
              cx={CX}
              cy={CY}
              r={MAX_R * f}
              fill="none"
              stroke="#F0F2F4"
              strokeWidth={1}
            />
          ))}

          {wedges.map((w) => {
            const dim = activeName && activeName !== w.name;
            return (
              <g key={w.name} opacity={dim ? 0.4 : 1} style={{ transition: "opacity 120ms" }}>
                <line
                  x1={w.leaderX}
                  y1={w.leaderY}
                  x2={w.lEndX}
                  y2={w.lEndY}
                  stroke={w.color}
                  strokeWidth={1.5}
                />
                <path
                  d={w.path}
                  fill={w.color}
                  stroke="#fff"
                  strokeWidth={2}
                  onMouseEnter={() => setHovered(w.name)}
                  onClick={() =>
                    setFocused((prev) => (prev === w.name ? null : w.name))
                  }
                  style={{ cursor: "pointer" }}
                >
                  <title>{`${w.name}: ${valueFormatter(w.value)}`}</title>
                </path>
                <text
                  x={w.tx}
                  y={w.ly}
                  textAnchor={w.anchor}
                  dominantBaseline="middle"
                  fontSize={11.5}
                  fontWeight={w.isHighlight ? 700 : 500}
                  fill={INK}
                  style={{ pointerEvents: "none" }}
                >
                  <tspan fontWeight={700} fill={w.color}>
                    {w.name}
                  </tspan>
                  <tspan fill={SUB} fontWeight={500}>{`  ${valueFormatter(
                    w.value,
                  )}`}</tspan>
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
};

export default NightingaleChart;
